import DoneAllIcon from "@mui/icons-material/DoneAll";
import HearingIcon from "@mui/icons-material/Hearing";
import HearingDisabledIcon from "@mui/icons-material/HearingDisabled";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import React, { useContext, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import FileFragment from "@/components/FeedItem/FileFragments";
import { formatChipTime } from "@/components/feed/tmsEvent/TmsEventChip";
import TmsEventDetailPopover from "@/components/feed/tmsEvent/TmsEventDetailPopover";
import { tmsEventDetailOf } from "@/components/feed/tmsEvent/tmsEventDetail";
import { tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import LinkFragment from "@/components/LinkFragment";
import type {
  HeardReason,
  ListenReceipt,
  MessageActivity,
  PlayedReason,
  ThreadEvent,
  ThreadMessage,
} from "@/hooks/dispatch/dispatchThread";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { zinc } from "@/ui/tokens";
import { redColor } from "@/utils/color";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";

/**
 * The thread's palette: the driver's bubbles sit back, the dispatcher's lean blue, receipts and audio read green, and
 * a playback the driver may not have heard reads orange.
 */
export const THREAD_COLORS = {
  driverBubble: "#26262c",
  dispatchBubble: "#1c2a45",
  dispatchBorder: "#2f4a7a",
  green: "#4ade80",
  orange: "#fb923c",
  eventPurple: "#c084fc",
} as const;

/** What the message details say about a playback that was only played, under its "Played" line. */
export const PLAYED_REASON_TEXT: Record<PlayedReason, string> = {
  deviceMuted: "Device volume was muted when message played",
  driverMaybeAbsent: "Message played but driver may not be present",
};

/** What the message details say about how a heard playback reached the driver, under its "Heard" line. */
export const HEARD_REASON_TEXT: Record<HeardReason, string> = {
  driving: "Message played while driver was driving",
  manualPlay: "Driver manually listened to message",
  repeated: "Driver repeated the message",
};

/** `HH:mm` for a caption. */
export function clockFor(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : format(date, "HH:mm");
}

/** `MMM d, yyyy, HH:mm` for a message caption: the classic timeline's date with the Dispatch page's 24-hour clock. */
export function datedClockFor(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : format(date, "MMM d, yyyy, HH:mm");
}

/** `m:ss` for a clip length. */
export function clipLength(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * The "Listen" pill on a message with audio. It drives the app's one audio player: a press on the active message
 * pauses or resumes it, a press on any other starts that one, so the thread never plays two things at once.
 */
function ListenButton({ itemId, seconds }: { itemId: string; seconds: number }) {
  const { activeItemId, playerPlaying, playItem, pauseItem, setActiveItemId } = useContext(AudioAppContext);
  const active = activeItemId === itemId;
  const playing = active && playerPlaying;
  const onClick = () => {
    if (playing) {
      pauseItem();
      return;
    }
    if (active) {
      playItem();
      return;
    }
    setActiveItemId(itemId);
  };
  return (
    <ButtonBase
      data-sb={TestIds.dispatch.threadListen}
      aria-label={playing ? "Pause" : "Listen"}
      aria-pressed={playing}
      onClick={onClick}
      sx={{
        mt: 0.75,
        px: 1,
        height: 22,
        gap: 0.5,
        borderRadius: 11,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: THREAD_COLORS.green,
        border: `1px solid ${THREAD_COLORS.green}`,
        bgcolor: "rgba(74, 222, 128, 0.08)",
        "&:hover": { bgcolor: "rgba(74, 222, 128, 0.16)" },
      }}
    >
      {playing ? <PauseIcon sx={{ fontSize: 14 }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
      {playing ? "Pause" : "Listen"} {clipLength(seconds)}
    </ButtonBase>
  );
}

const ACTIVITY_LABELS: Record<MessageActivity["kind"], string> = {
  seen: "Seen",
  heard: "Heard",
  played: "Played",
  acknowledged: "Acknowledged",
};

/**
 * The icon for a listen receipt: an ear for heard, a muted speaker or a struck ear for a playback only played. It
 * reads only the kind and a played reason, so a message's receipt and an activity line both fit.
 */
function ListenReceiptIcon({
  receipt,
  size,
}: {
  receipt: { kind: "heard" } | { kind: "played"; reason: PlayedReason };
  size: number;
}) {
  if (receipt.kind === "heard") return <HearingIcon sx={{ fontSize: size, color: THREAD_COLORS.green }} />;
  const sx = { fontSize: size, color: THREAD_COLORS.orange };
  return receipt.reason === "deviceMuted" ? <VolumeOffIcon sx={sx} /> : <HearingDisabledIcon sx={sx} />;
}

function ActivityIcon({ entry }: { entry: MessageActivity }) {
  if (entry.kind === "heard" || entry.kind === "played") return <ListenReceiptIcon receipt={entry} size={14} />;
  const sx = { fontSize: 14, color: THREAD_COLORS.green };
  if (entry.kind === "acknowledged") return <DoneAllIcon sx={sx} />;
  return <VisibilityIcon sx={sx} />;
}

/**
 * The driver's listen receipt in a message's caption: "Heard" in green, or "Played" in orange when the driver may not
 * have heard it, with the reason as the chip's tooltip; the activity list spells the reason out.
 */
function ListenReceiptChip({ receipt }: { receipt: ListenReceipt }) {
  const chip = (
    <Typography
      component="span"
      data-sb={TestIds.dispatch.threadReceipt}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        fontSize: "inherit",
        color: receipt.kind === "heard" ? THREAD_COLORS.green : THREAD_COLORS.orange,
      }}
    >
      <ListenReceiptIcon receipt={receipt} size={13} /> {ACTIVITY_LABELS[receipt.kind]}
    </Typography>
  );
  if (receipt.kind === "heard") return chip;
  return <Tooltip title={PLAYED_REASON_TEXT[receipt.reason]}>{chip}</Tooltip>;
}

/**
 * The activity button in a message's caption and the list it opens: every receipt on the message, oldest first, each
 * saying what happened, who did it, and when; the driver's listen receipt says under its line how the playback reached
 * them, or why they may not have heard it. Each is dated as the caption is, since a receipt can be days younger than
 * its message. Acknowledgements of one message live here rather than in the thread's event log, which is for the
 * channel.
 */
function ActivityButton({ activity }: { activity: readonly MessageActivity[] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <ButtonBase
        data-sb={TestIds.dispatch.threadActivity}
        aria-label="Message activity"
        aria-expanded={anchor != null}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          px: 0.5,
          height: 16,
          borderRadius: 0.5,
          fontSize: "inherit",
          color: zinc[500],
          "&:hover": { color: zinc[200], bgcolor: zinc[800] },
        }}
      >
        {activity.length === 0 ? "activity" : `${activity.length} ${activity.length === 1 ? "receipt" : "receipts"}`}
      </ButtonBase>
      <Popover
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.threadActivityList,
            sx: { bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, p: 1, minWidth: 220 },
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        {activity.length === 0 ? (
          <Typography sx={{ px: 0.5, py: 0.25, fontSize: "0.75rem", color: zinc[400] }}>No activity yet</Typography>
        ) : (
          activity.map((entry) => (
            <Box
              key={`${entry.kind}:${entry.actorName ?? ""}:${entry.at}`}
              sx={{ px: 0.5, py: 0.375, fontSize: "0.75rem" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ActivityIcon entry={entry} />
                <Box component="span" sx={{ color: zinc[100] }}>
                  {ACTIVITY_LABELS[entry.kind]}
                  {entry.actorName ? ` · ${entry.actorName}` : ""}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ml: "auto",
                    pl: 1.5,
                    color: zinc[500],
                    fontFamily: "ui-monospace, Menlo, monospace",
                    whiteSpace: "nowrap",
                  }}
                >
                  {datedClockFor(entry.at)}
                </Box>
              </Box>
              {entry.kind === "played" && (
                <Typography sx={{ pl: 2.75, fontSize: "0.6875rem", color: THREAD_COLORS.orange }}>
                  {PLAYED_REASON_TEXT[entry.reason]}
                </Typography>
              )}
              {entry.kind === "heard" && entry.reason && (
                <Typography sx={{ pl: 2.75, fontSize: "0.6875rem", color: zinc[400] }}>
                  {HEARD_REASON_TEXT[entry.reason]}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Popover>
    </>
  );
}

/**
 * One message in the thread: the driver's on the left in grey, anyone else's on the right in blue, each with its text,
 * its files and links, a Listen pill when there is audio, and a caption saying who and when, with the activity button
 * that opens who saw, heard, and acknowledged it. A dispatcher's message carries the driver's receipts in the caption:
 * Seen, and Heard or Played; a driver's message carries the red dot while no dispatcher has handled it. A message with
 * nothing to show yet says whether its content is still on its way from the server or still being transcribed.
 */
export const ThreadMessageBubble = React.memo(function ThreadMessageBubble({ message }: { message: ThreadMessage }) {
  const mine = !message.fromDriver;
  const who = message.fromDriver ? "Driver" : (message.senderName ?? "Dispatch");
  const html = message.text ? sanitizeHtmlForDisplay(message.text) : "";
  return (
    <Box
      data-sb={TestIds.dispatch.threadMessage}
      sx={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start", px: 2, py: 0.5 }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxWidth: "78%" }}>
        {!message.fromDriver || message.acknowledged ? null : (
          <Box
            aria-label="Unacknowledged"
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: redColor, mt: 1.75, flex: "none" }}
          />
        )}
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: mine ? THREAD_COLORS.dispatchBubble : THREAD_COLORS.driverBubble,
            border: `1px solid ${mine ? THREAD_COLORS.dispatchBorder : zinc[700]}`,
            color: zinc[100],
            fontSize: "0.875rem",
            lineHeight: 1.45,
            minWidth: 0,
          }}
        >
          {html && <Box className="display-transcript" dangerouslySetInnerHTML={{ __html: html }} />}
          {message.files.map((file) => (
            <Box key={file.id} sx={{ mt: html ? 1 : 0 }}>
              {FileFragment(file)}
            </Box>
          ))}
          {message.links.map((link) => (
            <Box key={link.id} sx={{ mt: 1 }}>
              {LinkFragment(link)}
            </Box>
          ))}
          {!html && message.files.length === 0 && message.links.length === 0 && (
            <Typography sx={{ fontSize: "inherit", color: zinc[500], fontStyle: "italic" }}>
              {message.audio ? "Voice message" : message.contentLoaded ? "Processing…" : "Loading…"}
            </Typography>
          )}
          {message.audio && <ListenButton itemId={message.id} seconds={message.audio.seconds} />}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 0.5,
          pl: message.fromDriver && !message.acknowledged ? 2 : 0,
          fontSize: "0.6875rem",
          color: zinc[500],
        }}
      >
        <Typography component="span" sx={{ fontSize: "inherit" }}>
          {who} · {datedClockFor(message.at)}
        </Typography>
        {mine && message.seen && (
          <Typography
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              fontSize: "inherit",
              color: THREAD_COLORS.green,
            }}
          >
            <DoneAllIcon sx={{ fontSize: 13 }} /> Seen
          </Typography>
        )}
        {mine && message.listened && <ListenReceiptChip receipt={message.listened} />}
        <ActivityButton activity={message.activity} />
      </Box>
    </Box>
  );
});

/**
 * A TMS event between the messages, as the catalog's one-line chip: the same text the driver feed's chip shows, so a
 * dispatcher reads the same event the same way on both surfaces, and opens the same detail on click when it has any.
 * WEB-2277 colors it by category.
 */
function TmsEventEntry({ event }: { event: Extract<ThreadEvent, { event: "tmsEvent" }> }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const text = tmsEventChipText(event.tmsEvent, formatChipTime);
  const hasDetail = tmsEventDetailOf(event.tmsEvent) !== null;
  return (
    <Box data-sb={TestIds.dispatch.threadEvent} sx={{ display: "flex", justifyContent: "center", px: 2, py: 0.75 }}>
      <Tooltip title={text}>
        <Box
          role={hasDetail ? "button" : undefined}
          tabIndex={hasDetail ? 0 : undefined}
          onClick={
            hasDetail
              ? (click: React.MouseEvent<HTMLElement>) => {
                  click.stopPropagation();
                  setAnchorEl(click.currentTarget);
                }
              : undefined
          }
          onKeyDown={
            hasDetail
              ? (key: React.KeyboardEvent<HTMLElement>) => {
                  if (key.key !== "Enter" && key.key !== " ") return;
                  key.preventDefault();
                  key.stopPropagation();
                  setAnchorEl(key.currentTarget);
                }
              : undefined
          }
          sx={{
            cursor: hasDetail ? "pointer" : undefined,
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: 0.5,
            borderRadius: 13,
            border: `1px solid ${zinc[700]}`,
            bgcolor: zinc[800],
            fontSize: "0.6875rem",
            color: zinc[300],
            maxWidth: "78%",
          }}
        >
          {text}
        </Box>
      </Tooltip>
      <TmsEventDetailPopover event={event.tmsEvent} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
    </Box>
  );
}

/** The divider before a day's first entry in the thread: its date between two rules, as the classic timeline has. */
export function ThreadDateDivider({ label }: { label: string }) {
  return (
    <Box
      data-sb={TestIds.dispatch.threadDate}
      role="separator"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: zinc[500],
        "&::before, &::after": { content: '""', flex: 1, borderTop: `1px solid ${zinc[800]}` },
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One event in the thread, drawn as a centered pill between the messages: a channel acknowledgement naming who handled
 * the channel and when, or a TMS event from the driver's day. Its time carries no date: the day's divider dates it.
 */
export const ThreadEventPill = React.memo(function ThreadEventPill({ event }: { event: ThreadEvent }) {
  if (event.event === "tmsEvent") return <TmsEventEntry event={event} />;
  const label = "Acknowledged channel";
  return (
    <Box data-sb={TestIds.dispatch.threadEvent} sx={{ display: "flex", justifyContent: "center", px: 2, py: 0.75 }}>
      <Tooltip title={event.actorName ? `${label} by ${event.actorName}` : label}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            height: 26,
            borderRadius: 13,
            border: `1px solid ${THREAD_COLORS.dispatchBorder}`,
            bgcolor: "rgba(47, 74, 122, 0.25)",
            fontSize: "0.6875rem",
            color: zinc[300],
          }}
        >
          <DoneAllIcon sx={{ fontSize: 14, color: THREAD_COLORS.green }} />
          <Box component="span" sx={{ color: THREAD_COLORS.green, fontWeight: 600 }}>
            {label}
          </Box>
          <Box component="span" sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {clockFor(event.at)}
          </Box>
          {event.actorName && <Box component="span">· {event.actorName}</Box>}
        </Box>
      </Tooltip>
    </Box>
  );
});
